import { useEffect, useRef, useState, type MutableRefObject } from "react";
import { AddressAutocompleteInput } from "@/components/common/AddressAutocompleteInput";
import { updateManufacturingPlant, type MfgPlant } from "@/lib/manufacturingClient";
import type { MfgVendor } from "@/lib/manufacturing/purchaseOrderClient";
import { GeneratePurchaseOrderNewVendor } from "@/components/manufacturing/GeneratePurchaseOrderNewVendor";
import { mfgXsInputTallClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import { formatStructuredAddressOneLine } from "@/lib/addressValidation/addressFormat";
import { listOrgLocationsApi } from "@/lib/orgLocations/orgLocationsClient";
import type { OrgLocation } from "@/lib/orgLocations/orgLocationsTypes";
import { formatLocationShipAddress, pickPlantLocation } from "@/lib/manufacturing/poShipTo";
import type { MfgVendorExtraContact } from "@/lib/manufacturing/vendorExtraContacts";

/** Null when the plant address is saved (or unchanged). A string is the save error. */
export type SavePlantDeliveryAddress = () => Promise<string | null>;

export function GeneratePurchaseOrderHeader(props: {
  vendors: MfgVendor[];
  plants: MfgPlant[];
  vendorId: string;
  setVendorId: (v: string) => void;
  plantId: string;
  setPlantId: (v: string) => void;
  shipTo: string;
  setShipTo: (v: string) => void;
  shipToOpen: boolean;
  setShipToOpen: (v: boolean) => void;
  poDate: string;
  setPoDate: (v: string) => void;
  enabled: boolean;
  busy: boolean;
  readOnlyHeader: boolean;
  onVendorCreated: (vendor: MfgVendor) => void;
  onPlantUpdated?: (plant: MfgPlant) => void;
  mergeVendorCc?: (contacts: MfgVendorExtraContact[] | undefined) => void;
  savePlantDeliveryAddressRef?: MutableRefObject<SavePlantDeliveryAddress>;
}) {
  const [addingVendor, setAddingVendor] = useState(false);
  const [locations, setLocations] = useState<OrgLocation[]>([]);
  const plant = props.plants.find((p) => p.id === props.plantId);
  const storedAddress = plant?.delivery_address?.trim() ?? "";
  const [deliveryAddress, setDeliveryAddress] = useState(storedAddress);
  const [addressMsg, setAddressMsg] = useState("");
  const editedPlantId = useRef<string | null>(null);
  const seenPlantId = useRef(props.plantId);
  useEffect(() => {
    void listOrgLocationsApi().then((res) => {
      if (res.ok) setLocations(res.data.locations ?? []);
    });
  }, []);
  useEffect(() => {
    if (seenPlantId.current !== props.plantId) {
      seenPlantId.current = props.plantId;
      editedPlantId.current = null;
    }
    if (editedPlantId.current === props.plantId) return;
    if (storedAddress) {
      setDeliveryAddress(storedAddress);
      setAddressMsg("");
      return;
    }
    if (!plant) {
      setDeliveryAddress("");
      setAddressMsg("");
      return;
    }
    const match = pickPlantLocation(locations, { plant_code: plant.plant_code, name: plant.name });
    setDeliveryAddress(match ? formatLocationShipAddress(match) : "");
    setAddressMsg("");
  }, [plant, storedAddress, locations, props.plantId]);
  const canAddVendor = props.enabled && !props.readOnlyHeader;
  const locked = !props.enabled || props.busy || props.readOnlyHeader;

  async function saveDeliveryAddress(): Promise<string | null> {
    if (!plant || !props.enabled || props.readOnlyHeader) return null;
    const next = deliveryAddress.trim();
    if (next === (plant.delivery_address ?? "").trim()) return null;
    setAddressMsg("");
    const res = await updateManufacturingPlant({ plant_id: plant.id, delivery_address: next });
    if (!res.ok) {
      setAddressMsg(res.error);
      return res.error;
    }
    props.onPlantUpdated?.(res.data.plant);
    setAddressMsg(next ? "Saved on the plant. It prints in Ship to." : "Cleared.");
    return null;
  }

  const saveDeliveryAddressRef = useRef(saveDeliveryAddress);
  saveDeliveryAddressRef.current = saveDeliveryAddress;
  useEffect(() => {
    const slot = props.savePlantDeliveryAddressRef;
    if (!slot) return;
    slot.current = () => saveDeliveryAddressRef.current();
  }, [props.savePlantDeliveryAddressRef]);

  return (
    <div className="grid grid-cols-2 gap-2">
      <div className="text-xs">
        <div className="flex items-center justify-between gap-2">
          <span className="font-semibold">Vendor</span>
          {canAddVendor ? (
            <button
              type="button"
              className="text-[11px] font-semibold text-indigo-700 hover:underline dark:text-indigo-300"
              onClick={() => setAddingVendor((open) => !open)}
            >
              {addingVendor ? "Cancel" : "+ New"}
            </button>
          ) : null}
        </div>
        <select
          className={`${mfgXsInputTallClass} mt-1 w-full`}
          value={props.vendorId}
          disabled={locked}
          aria-label="Vendor"
          onChange={(e) => {
            const id = e.target.value;
            props.setVendorId(id);
            props.mergeVendorCc?.(props.vendors.find((v) => v.id === id)?.extra_contacts);
          }}
        >
          <option value="">Select vendor…</option>
          {props.vendors.map((v) => (
            <option key={v.id} value={v.id}>
              {v.name}
            </option>
          ))}
        </select>
      </div>
      <label className="text-xs">
        <span className="font-semibold">Ship-to plant</span>
        <select
          className={`${mfgXsInputTallClass} mt-1 w-full`}
          value={props.plantId}
          disabled={locked}
          onChange={(e) => props.setPlantId(e.target.value)}
        >
          {props.plants.map((p) => (
            <option key={p.id} value={p.id}>
              {p.plant_code} · {p.name}
            </option>
          ))}
        </select>
      </label>
      <label className="col-span-2 text-xs">
        <span className="font-semibold">Plant delivery address</span>
        <textarea
          className={`${mfgXsInputTallClass} mt-1 w-full`}
          rows={2}
          value={deliveryAddress}
          disabled={locked || !plant}
          placeholder="Street, city, state, ZIP — vendors deliver here"
          onChange={(e) => {
            editedPlantId.current = props.plantId;
            setDeliveryAddress(e.target.value);
          }}
          onBlur={() => void saveDeliveryAddress()}
        />
        <span className="mt-0.5 block text-[11px] font-normal text-gray-500">
          Required before issue. Saved on the plant and printed on the PO.
          {addressMsg ? ` ${addressMsg}` : ""}
        </span>
      </label>
      <label className="text-xs">
        <span className="font-semibold">PO date</span>
        <input
          type="date"
          className={`${mfgXsInputTallClass} mt-1 w-full`}
          value={props.poDate}
          disabled={locked}
          onChange={(e) => props.setPoDate(e.target.value)}
        />
      </label>
      <details
        className="relative z-20 overflow-visible text-xs"
        open={props.shipToOpen}
        onToggle={(e) => props.setShipToOpen((e.currentTarget as HTMLDetailsElement).open)}
      >
        <summary className="cursor-pointer select-none font-semibold text-gray-800 dark:text-zinc-100">
          Ship-to override
          <span className="ml-1 font-normal text-gray-500 dark:text-zinc-400">
            {props.shipTo.trim() ? "(set)" : "(optional)"}
          </span>
        </summary>
        <div className="mt-1">
          <AddressAutocompleteInput
            value={props.shipTo}
            onChange={props.setShipTo}
            onPick={(pick) => {
              props.setShipTo(
                pick.formatted.trim() || formatStructuredAddressOneLine(pick.normalized),
              );
            }}
            disabled={locked}
            className={`${mfgXsInputTallClass} w-full`}
            placeholder="Start typing a street address…"
            hintWhenDisabled="Autocomplete unavailable — type the ship-to address."
          />
        </div>
      </details>
      {addingVendor && canAddVendor ? (
        <div className="col-span-2">
          <GeneratePurchaseOrderNewVendor
            enabled={props.enabled}
            busy={props.busy}
            onCreated={(vendor) => {
              props.onVendorCreated(vendor);
              props.mergeVendorCc?.(vendor.extra_contacts);
              setAddingVendor(false);
            }}
            onCancel={() => setAddingVendor(false)}
          />
        </div>
      ) : null}
    </div>
  );
}
